import { memo, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Flag, CircleStop, Check, X, Focus, Settings, Handshake, LogOut } from 'lucide-react';
import { FlipBoardButton } from './FlipBoardButton';
import '../shared/offer-bar.css';
import './GameToolbar.css';
import { ConfirmDialog } from '../common/ConfirmDialog';

export interface GameToolbarProps {
  onResign: () => void;
  onOfferDraw: () => void;
  onAcceptDraw?: () => void;
  onDeclineDraw?: () => void;
  drawOffered: boolean;
  opponentOfferedDraw: boolean;
  // Takeback (casual online): opponent's incoming request.
  opponentRequestedTakeback?: boolean;
  pendingTakebackPlies?: number | null;
  onAcceptTakeback?: () => void;
  onDeclineTakeback?: () => void;
  disabled?: boolean;
  onToggleFocus?: () => void;
  isFocusOn?: boolean;
  /**
   * Prende il posto di Focus quando c'è una scelta più urgente da offrire (il
   * boost d'arena, che vale solo prima della prima mossa). La barra resta a
   * quattro posti — a 360px non c'è larghezza per un quinto — e chi passa lo
   * slot decide *quando*: qui arriva già solo se va mostrato, altrimenti
   * `undefined` e Focus torna al suo posto.
   */
  focusSlot?: ReactNode;
  onOpenSettings?: () => void;
  /**
   * Viewer is watching, not playing: hide the actions that belong to a player
   * (resign, draw) and every incoming-offer prompt, which is a private
   * negotiation between the two players. Focus goes too — zen hides the move
   * list, the header and the player info, which is exactly what someone came to
   * watch. Settings stays: it is about how the board is drawn.
   */
  readOnly?: boolean;
  /**
   * Leave, shown only in `readOnly`. A spectator has no Resign to fall back on
   * and the app hides its sidebar on a game screen, so without this the only
   * way out is the browser's back gesture — which a standalone PWA may not have.
   */
  onLeave?: () => void;
  /**
   * Gira la damiera, anche lei solo in `readOnly`. Chi guarda entra dal lato del
   * giocatore che ha scelto e prima non aveva modo di cambiarlo.
   *
   * Non passa dal `focusSlot` (che pure il bottone lo userebbe, in presenza): quel
   * posto e' dichiarato una scelta DA GIOCATORE — il boost d'arena, lo sbircia alla
   * cieca — ed e' spento in `readOnly`. In piu' `focusSlot` lo si spegne a partita
   * finita, mentre questo deve restare: davanti a una posizione finale ferma girare
   * la damiera e' proprio quando serve.
   */
  onFlipBoard?: () => void;
  /**
   * La patta chiede conferma prima di partire. Serve in presenza, dove `onOfferDraw`
   * non propone niente a nessuno: chiude la partita in parita' all'istante, ed e'
   * irreversibile come il ritiro. Online resta una proposta, e non si conferma.
   */
  confirmDraw?: boolean;
  /**
   * Il bottone Patta e' spento dal FRENO alle proposte, e queste due stringhe
   * dicono perche'.
   *
   * Due stringhe primitive e non un oggetto: questo componente e' `memo`, e un
   * oggetto nuovo a ogni render del padre la vanificherebbe.
   *
   * - `drawBlockedLabel` prende il posto di «Patta» sul bottone. BREVE, due
   *   parole: sotto i 480px il CSS manda a capo centrato (e' fatto per
   *   «Abbandona» e «Impostazioni»), ma la barra regge quattro posti e non uno
   *   di piu'. Un bottone spento che dice ancora «Patta» non spiega niente a
   *   chi non ha un mouse da passarci sopra.
   * - `drawBlockedReason` e' il motivo per intero, su `title` e `aria-label`.
   *
   * Non passarle lascia il bottone identico a prima: e' cosi' che la partita
   * locale, quella contro il computer e la presenza non vedono il freno, che
   * del resto non le riguarda — li' nessuno propone niente a nessuno.
   */
  drawBlockedLabel?: string | null;
  drawBlockedReason?: string | null;
  /**
   * In presenza il primo bottone non e' una resa: chiude la partita SENZA
   * vincitore.
   *
   * Fra due persone a questo dispositivo non c'e' nessuno a cui consegnarla —
   * il «tu» di «perderai la partita» sono tutt'e due — quindi cambiano la
   * parola, l'icona e la conferma. Il posto nella barra e il comportamento
   * (conferma, poi `onResign`) restano quelli: e' lo stesso gesto, detto
   * giusto. Sorella di `confirmDraw` qui sopra, che esiste per la stessa
   * ragione.
   */
  senzaVincitore?: boolean;
}

export const GameToolbar = memo<GameToolbarProps>(
  ({ onResign, onOfferDraw, onAcceptDraw, onDeclineDraw, drawOffered, opponentOfferedDraw, opponentRequestedTakeback = false, pendingTakebackPlies = null, onAcceptTakeback, onDeclineTakeback, disabled, onToggleFocus, isFocusOn = false, focusSlot, onOpenSettings, readOnly = false, onLeave, onFlipBoard, confirmDraw = false, drawBlockedLabel = null, drawBlockedReason = null, senzaVincitore = false }) => {
    const { t } = useTranslation();
    const [showResignConfirm, setShowResignConfirm] = useState(false);
    const [showDrawConfirm, setShowDrawConfirm] = useState(false);

    /*
     * La resa e la chiusura senza vincitore sono lo stesso posto nella barra, e
     * cambiano solo nelle parole: una parola sola sul bottone (a 360px la barra
     * regge quattro posti e «Abbandona» ci va a capo), la frase intera nella
     * conferma. L'icona segue: la bandiera e' la resa, il cerchio e' uno stop —
     * e non `LogOut`, che qui dentro e' gia' l'uscita di chi guarda.
     */
    const etichettaResa = senzaVincitore
      ? t('game.actions.endGame', 'End')
      : t('game.actions.resign', 'Resign');

    if (opponentOfferedDraw && !readOnly) {
      return (
        <div className="game-toolbar game-toolbar--draw-offer offer-bar" data-testid="game-toolbar">
          <span className="game-toolbar__draw-text offer-bar__text">{t('game.actions.drawOffered', 'Draw offered')}</span>
          <button
            type="button"
            className="game-toolbar__btn game-toolbar__btn--accept offer-bar__btn--accept"
            data-testid="toolbar-draw-accept"
            onClick={onAcceptDraw}
            disabled={disabled}
          >
            <Check size={18} aria-hidden="true" />
            <span>{t('game.actions.acceptDraw', 'Accept')}</span>
          </button>
          <button
            type="button"
            className="game-toolbar__btn game-toolbar__btn--decline offer-bar__btn--decline"
            data-testid="toolbar-draw-decline"
            onClick={onDeclineDraw}
            disabled={disabled}
          >
            <X size={18} aria-hidden="true" />
            <span>{t('game.actions.declineDraw', 'Decline')}</span>
          </button>
        </div>
      );
    }

    // Takeback request from the opponent (casual online). Draw takes precedence above.
    if (opponentRequestedTakeback && !readOnly) {
      return (
        <div className="game-toolbar game-toolbar--draw-offer offer-bar" data-testid="game-toolbar">
          <span className="game-toolbar__draw-text offer-bar__text">
            {t('game.actions.opponentRequestsTakeback', { count: pendingTakebackPlies ?? 0 })}
          </span>
          <button
            type="button"
            className="game-toolbar__btn game-toolbar__btn--accept offer-bar__btn--accept"
            data-testid="toolbar-takeback-accept"
            onClick={onAcceptTakeback}
            disabled={disabled}
          >
            <Check size={18} aria-hidden="true" />
            <span>{t('game.actions.acceptTakeback', 'Accept')}</span>
          </button>
          <button
            type="button"
            className="game-toolbar__btn game-toolbar__btn--decline offer-bar__btn--decline"
            data-testid="toolbar-takeback-decline"
            onClick={onDeclineTakeback}
            disabled={disabled}
          >
            <X size={18} aria-hidden="true" />
            <span>{t('game.actions.declineTakeback', 'Decline')}</span>
          </button>
        </div>
      );
    }

    return (
      <div className="game-toolbar" data-testid="game-toolbar">
        {readOnly && onLeave && (
          <button
            type="button"
            className="game-toolbar__btn game-toolbar__btn--leave"
            data-testid="toolbar-leave"
            onClick={onLeave}
            title={t('spectate.leave', 'Leave')}
          >
            <LogOut size={20} aria-hidden="true" />
            <span>{t('spectate.leave', 'Leave')}</span>
          </button>
        )}
        {readOnly && onFlipBoard && <FlipBoardButton onClick={onFlipBoard} />}
        {!readOnly && (
          <button
            type="button"
            className="game-toolbar__btn game-toolbar__btn--resign"
            data-testid="toolbar-resign"
            onClick={() => setShowResignConfirm(true)}
            disabled={disabled}
            title={etichettaResa}
          >
            {senzaVincitore
              ? <CircleStop size={20} aria-hidden="true" />
              : <Flag size={20} aria-hidden="true" />}
            <span>{etichettaResa}</span>
          </button>
        )}
        {!readOnly && (
          <button
            type="button"
            className="game-toolbar__btn game-toolbar__btn--draw"
            data-testid="toolbar-draw"
            onClick={() => (confirmDraw ? setShowDrawConfirm(true) : onOfferDraw())}
            disabled={drawOffered || disabled || !!drawBlockedReason}
            /* Il motivo anche su `aria-label`: su un bottone spento il `title`
               non si legge col dito, e chi usa un lettore di schermo non ha
               nessun altro modo di saperlo. */
            title={drawBlockedReason ?? t('game.actions.offerDraw', 'Draw')}
            {...(drawBlockedReason ? { 'aria-label': drawBlockedReason } : {})}
          >
            <Handshake size={20} aria-hidden="true" />
            <span>
              {drawOffered
                ? t('game.actions.drawSent', 'Draw sent')
                : (drawBlockedLabel ?? t('game.actions.offerDraw', 'Draw'))}
            </span>
          </button>
        )}
        {!readOnly && focusSlot}
        {/* `|| isFocusOn`: quando `focusSlot` e' occupato (il boost d'arena, «Gira»
            in presenza) il bottone Focus non si rendeva — ma lo zen nasconde ogni
            bottone TRANNE `--focus`, quindi chi entrava in focus e poi vedeva
            comparire il boost restava chiuso dentro senza via d'uscita. In focus
            la via d'uscita conta piu' del posto risparmiato: si mostrano
            entrambi. */}
        {!readOnly && (!focusSlot || isFocusOn) && (
          <button
            type="button"
            className={`game-toolbar__btn game-toolbar__btn--focus${isFocusOn ? ' game-toolbar__btn--focus-active' : ''}`}
            data-testid="toolbar-focus"
            data-focus-on={isFocusOn ? 'true' : 'false'}
            onClick={onToggleFocus}
            disabled={disabled}
            title={t('game.actions.focus', 'Focus')}
          >
            <Focus size={20} aria-hidden="true" />
            <span>
              {isFocusOn ? t('game.actions.focusExit', 'Exit') : t('game.actions.focus', 'Focus')}
            </span>
          </button>
        )}
        {onOpenSettings && (
          <button
            type="button"
            className="game-toolbar__btn game-toolbar__btn--settings"
            data-testid="toolbar-settings"
            onClick={onOpenSettings}
            title={t('profile.settings', 'Settings')}
          >
            <Settings size={20} aria-hidden="true" />
            <span>{t('profile.settings', 'Settings')}</span>
          </button>
        )}

        {showResignConfirm && (
          <ConfirmDialog
            title={senzaVincitore
              ? t('game.confirmEndGameTitle', 'End the game?')
              : t('game.confirmResignTitle', 'Resign?')}
            message={senzaVincitore
              ? t('game.confirmEndGame', 'The game will end with no winner.')
              : t('game.confirmResign', 'Are you sure you want to resign? You will lose the game.')}
            confirmLabel={etichettaResa}
            confirmTestId="toolbar-resign-confirm"
            cancelTestId="toolbar-resign-cancel"
            onConfirm={() => { setShowResignConfirm(false); onResign(); }}
            onCancel={() => setShowResignConfirm(false)}
          />
        )}

        {showDrawConfirm && (
          <ConfirmDialog
            title={t('game.confirmDrawTitle', 'Draw?')}
            message={t('game.confirmDraw', 'End the game as a draw?')}
            confirmLabel={t('game.actions.offerDraw', 'Draw')}
            confirmTestId="toolbar-draw-confirm"
            cancelTestId="toolbar-draw-cancel"
            onConfirm={() => { setShowDrawConfirm(false); onOfferDraw(); }}
            onCancel={() => setShowDrawConfirm(false)}
          />
        )}
      </div>
    );
  }
);

GameToolbar.displayName = 'GameToolbar';
